import { formatPhoneNumber } from '@imprint-payments/imprint-lib';
import {
  colors,
  ImpButton,
  ImpContainer,
  ImpFlex,
  ImpOtpField,
  ImpPad,
  ImpSpacer,
  ImpText,
  spacing,
} from '@imprint-payments/imprint-ui';
import { useStatsigClient } from '@statsig/react-bindings';
import { useEffect, useState } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import useGetPrefillData from '../../api/getPrefillData/useGetPrefillData';
import { useResendOtp } from '../../api/login/useLogin';
import { API_RESPONSE_CODE } from '../../api/responseVals';
import { useVerifyOtp } from '../../api/verifyOtp.api';
import { useImpState } from '../../app/hooks';
import { SCREEN, setScreen } from '../../app/screenStateSlice';
import { ANALYTICS } from '../../firebase/firebase';
import Gates from '../../statsig/gates';
import { PAGE } from '../../utils/routeConstant';
import { useProveContext } from './ProveContext';

export default function EarlyOtp(props: { goToManualApp: () => void }) {
  const {
    consumerInfo,
    appScreenState,
    dispatch,
    language,
    navigate,
    merchantData,
  } = useImpState();
  const phone = consumerInfo.consumerProfile.phone;
  const verifyOtpMutation = useVerifyOtp();
  const resendOtpMutation = useResendOtp();
  const { optInPrefill } = useProveContext();
  const { checkGate } = useStatsigClient();

  const prefillDataMutation = useGetPrefillData();
  const [showErr, setShowErr] = useState(false);
  const [clear, setClear] = useState(false);

  const isLoading =
    verifyOtpMutation.isPending ||
    resendOtpMutation.isPending ||
    prefillDataMutation.isPending;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    ANALYTICS.logEvent(EVENT.OTP_VIEWED);
  }, []);

  return (
    <ImpPad fluid paddingLeft={'l'} paddingRight={'l'}>
      <ImpContainer textAlign={'center'}>
        <ImpPad paddingTop={'xl'} paddingBottom={'xl'}>
          <ImpFlex direction={'column'} fluid align={'center'} gap={spacing.xs}>
            <ImpText typography={'headline3'}>{language.enterOtpCode}</ImpText>
            <ImpText
              typography={'body2'}
              color={colors.content.onSurfaceVariant}
            >
              {language.verifyIdentityPhone.replace(
                '{phone}',
                formatPhoneNumber(phone)
              )}
            </ImpText>
            <ImpSpacer height={spacing.xl} />
            <ImpOtpField
              numInputs={6}
              onFull={handleCodeFull}
              clearWithError={showErr}
              clear={clear}
              onChange={(val) => {
                if (val.length > 0) {
                  setClear(false);
                  setShowErr(false);
                }
              }}
            />
          </ImpFlex>
        </ImpPad>
        {showErr && (
          <ImpText
            typography={'body2'}
            color={colors.content.onBackgroundError}
          >
            {language.phoneNoMatch}
          </ImpText>
        )}
        <ImpPad padding={spacing.l}>
          <ImpFlex direction={'column'} align={'center'}>
            <ImpText
              typography={'body3'}
              color={colors.content.onSurfaceVariant}
            >
              {language.didntReceiveCode}
            </ImpText>
            <ImpSpacer height={spacing.l} />
            <ImpButton
              buttonStyle={'branded'}
              variant={'secondary'}
              state={isLoading ? 'submitting' : 'enabled'}
              onClick={handleResendOTP}
              size="fill"
            >
              {language.resendCode}
            </ImpButton>
            <ImpSpacer height="l" />
          </ImpFlex>
        </ImpPad>
      </ImpContainer>
    </ImpPad>
  );

  function handleCodeFull(code: string) {
    ANALYTICS.logEvent(EVENT.OTP_SUBMITTED);
    setShowErr(false);
    verifyOtpMutation.mutate(
      { code },
      {
        onSuccess: (data) => {
          if (data.nextStep !== 'showSignUp') {
            ANALYTICS.logEvent(EVENT.OTP_VERIFIED, {
              context: data.nextStep,
            });
            return;
          }
          // Disable pre-fill in sandbox env for partner testing
          if (checkGate(Gates.ProveGate) && optInPrefill.current) {
            ANALYTICS.logEvent(EVENT.OTP_VERIFIED, {
              context: 'prove',
            });
            prefillDataMutation.mutate(
              {
                sessionId: appScreenState.proveSessionId,
                ssn4: consumerInfo.consumerProfile.ssn4 || '',
                productUUID: merchantData.productUUID,
              },
              {
                onSettled: () => {
                  props.goToManualApp();
                },
              }
            );
          } else {
            ANALYTICS.logEvent(EVENT.OTP_VERIFIED, {
              context: 'non_prove',
            });
            props.goToManualApp();
          }
        },
        onError: (err) => {
          if (err.code === API_RESPONSE_CODE.INCORRECT_AUTH) {
            setShowErr(true);
          } else if (err.code === API_RESPONSE_CODE.EXPIRED_AUTH) {
            dispatch(setScreen(SCREEN.STATUS_OTP_VERIFICATION_FAILED));
            navigate(PAGE.ACCOUNT_STATUS);
          }
        },
      }
    );
  }

  function handleResendOTP() {
    setClear(true);
    setShowErr(false);
    resendOtpMutation.mutate({
      phone: consumerInfo.consumerProfile.phone,
    });
  }
}
